/**
 * Atlas — Notifications inbox.
 *
 * Reads /notifications, lets the user mark items read (individually or
 * all at once), and routes them to the linked resource when they click
 * a row. Phase 1 only — no preferences UI yet, no per-channel toggles.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../services/api';

const KIND_ICONS = {
  'booking.quote_requested': '🛠️',
  'booking.quote_sent':      '💼',
  'booking.quote_accepted':  '✅',
  'booking.invoice_issued':  '🧾',
  'booking.message':         '💬',
  'eoi.match_received':      '🏠',
  'eoi.message':             '💬',
};

function timeAgo(iso) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(t).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await notificationsAPI.list(1, 50);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpen = async (item) => {
    // Optimistic update — flip the local row, then call mark-read so
    // the badge in the topbar decrements next poll.
    if (!item.is_read) {
      setItems((arr) => arr.map((x) => (x.id === item.id ? { ...x, is_read: true } : x)));
      setUnread((n) => Math.max(0, n - 1));
      notificationsAPI.markRead(item.id).catch(() => { /* sync next poll */ });
    }
    if (item.link_url) navigate(item.link_url);
  };

  const handleMarkAllRead = async () => {
    setBusy(true);
    try {
      await notificationsAPI.markAllRead();
      setItems((arr) => arr.map((x) => ({ ...x, is_read: true })));
      setUnread(0);
    } catch (_) { /* swallow — user can retry */ }
    finally { setBusy(false); }
  };

  return (
    <div className="page-wrap" style={{ maxWidth: 760 }}>
      <div className="page-header" style={{ marginBottom: 22 }}>
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            Inbox
          </div>
          <h1 className="serif" style={{ fontSize: 28, margin: '4px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
            Notifications
          </h1>
          <div style={{ fontSize: 13, color: 'var(--ink-4)', marginTop: 4 }}>
            {unread > 0 ? `${unread} unread of ${total}` : `${total} total`}
          </div>
        </div>
        {unread > 0 && (
          <button className="btn ghost sm" disabled={busy} onClick={handleMarkAllRead}>
            {busy ? '…' : 'Mark all read'}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: 'var(--ink-4)', textAlign: 'center', padding: 60 }}>Loading…</div>
      ) : error ? (
        <div style={{ background: '#fdecea', border: '1px solid #f5c6c6', borderRadius: 8, padding: '12px 16px', color: '#b71c1c', fontSize: 13 }}>
          {error}
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--ink-4)' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔕</div>
          <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)' }}>
            No notifications yet
          </div>
          <p style={{ fontSize: 13, maxWidth: 320, margin: '8px auto 0' }}>
            You'll see quote updates, EOI responses, invoices, and messages here.
          </p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden', background: 'var(--card)' }}>
          {items.map((it, i) => (
            <div
              key={it.id}
              onClick={() => handleOpen(it)}
              style={{
                display: 'flex', gap: 14, padding: '14px 18px',
                borderBottom: i < items.length - 1 ? '1px solid var(--hairline)' : 'none',
                cursor: it.link_url ? 'pointer' : 'default',
                background: it.is_read ? 'transparent' : 'rgba(31,58,46,0.04)',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(31,58,46,0.07)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = it.is_read ? 'transparent' : 'rgba(31,58,46,0.04)')}
            >
              <div style={{ fontSize: 22, flexShrink: 0, lineHeight: 1 }}>
                {KIND_ICONS[it.kind] || '🔔'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontWeight: it.is_read ? 500 : 700, fontSize: 14, color: 'var(--ink)' }}>
                    {it.title}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', flexShrink: 0 }}>
                    {timeAgo(it.created_at)}
                  </div>
                </div>
                {it.body && (
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.5 }}>
                    {it.body}
                  </div>
                )}
              </div>
              {!it.is_read && (
                <div style={{
                  width: 8, height: 8, borderRadius: 4,
                  background: 'var(--forest)', flexShrink: 0, marginTop: 8,
                }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
